import React, { useEffect, useRef } from 'react';
import './WindowAnimation.css';

const WindowAnimation = ({ isDarkMode }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const floatingElements = [];
    const rippleElements = [];

    // Create floating elements with enhanced dark mode
    const createFloatingElement = () => {
      const element = document.createElement('div');
      element.className = 'floating-element';

      // Enhanced color scheme for dark mode (Neon Teal, Electric Purple, Deep Navy)
      const darkModeColors = [
        'hsla(183, 100%, 50%, 0.15)',  // Neon Teal (#00F0FF)
        'hsla(271, 76%, 53%, 0.15)',   // Electric Purple (#8A2BE2)
        'hsla(200, 100%, 60%, 0.1)',   // Soft Cyan
        'hsla(280, 100%, 60%, 0.08)'   // Soft Magenta
      ];

      const lightModeColors = [
        'hsla(14, 100%, 66%, 0.12)',   // Sunrise Orange (#FF7B54)
        'hsla(0, 100%, 68%, 0.12)',    // Soft Coral (#FF5D5D)
        'hsla(40, 100%, 60%, 0.1)',    // Warm yellow
        'hsla(340, 100%, 70%, 0.08)'   // Soft pink
      ];

      const colors = isDarkMode ? darkModeColors : lightModeColors;
      const selectedColor = colors[Math.floor(Math.random() * colors.length)];

      // Random properties
      const size = Math.random() * 25 + 8;
      const x = Math.random() * window.innerWidth;
      const y = Math.random() * window.innerHeight;
      const duration = Math.random() * 25 + 15;
      const delay = Math.random() * 8;

      element.style.cssText = `
        position: fixed;
        width: ${size}px;
        height: ${size}px;
        left: ${x}px;
        top: ${y}px;
        border-radius: 50%;
        background: ${selectedColor};
        pointer-events: none;
        z-index: 1;
        animation: float ${duration}s ease-in-out infinite;
        animation-delay: ${delay}s;
        box-shadow: 0 0 30px ${selectedColor.replace('0.15', '0.4').replace('0.12', '0.3').replace('0.1', '0.25').replace('0.08', '0.2')};
        backdrop-filter: blur(1px);
      `;

      container.appendChild(element);
      floatingElements.push(element);
    };

    // Create ripple effect elements
    const createRippleElement = () => {
      const element = document.createElement('div');
      element.className = 'ripple-element';

      const size = Math.random() * 100 + 50;
      const x = Math.random() * window.innerWidth;
      const y = Math.random() * window.innerHeight;
      const duration = Math.random() * 8 + 4;

      element.style.cssText = `
        position: fixed;
        width: ${size}px;
        height: ${size}px;
        left: ${x}px;
        top: ${y}px;
        border: 2px solid ${isDarkMode ? 'hsla(180, 70%, 60%, 0.1)' : 'hsla(200, 70%, 50%, 0.08)'};
        border-radius: 50%;
        pointer-events: none;
        z-index: 0;
        animation: ripple ${duration}s ease-out infinite;
        animation-delay: ${Math.random() * 4}s;
      `;

      container.appendChild(element);
      rippleElements.push(element);
    };

    // Create magnetic field effect
    const createMagneticField = () => {
      const element = document.createElement('div');
      element.className = 'magnetic-field';

      const size = Math.random() * 200 + 100;
      const x = Math.random() * window.innerWidth;
      const y = Math.random() * window.innerHeight;

      element.style.cssText = `
        position: fixed;
        width: ${size}px;
        height: ${size}px;
        left: ${x}px;
        top: ${y}px;
        background: radial-gradient(circle, ${isDarkMode ? 'hsla(180, 70%, 60%, 0.05)' : 'hsla(200, 70%, 50%, 0.03)'} 0%, transparent 70%);
        border-radius: 50%;
        pointer-events: none;
        z-index: 0;
        animation: magnetic ${Math.random() * 20 + 10}s ease-in-out infinite;
        animation-delay: ${Math.random() * 5}s;
      `;

      container.appendChild(element);
    };

    // Create multiple elements
    for (let i = 0; i < 12; i++) {
      createFloatingElement();
    }

    for (let i = 0; i < 6; i++) {
      createRippleElement();
    }

    for (let i = 0; i < 4; i++) {
      createMagneticField();
    }

    // Track latest values to prevent blocking main thread
    let latestScrollY = window.pageYOffset;
    let latestMouseX = 0.5;
    let latestMouseY = 0.5;
    let ticking = false;

    // Cache the sections
    const sections = document.querySelectorAll('.card, .headding, .container');

    const updateTransforms = () => {
      const rate = latestScrollY * -0.3;

      floatingElements.forEach((element, index) => {
        const speed = (index + 1) * 0.08;
        const scrollMove = rate * speed;

        const mouseSpeed = (index + 1) * 0.015;
        const xMove = (latestMouseX - 0.5) * mouseSpeed * 60;
        const yMove = (latestMouseY - 0.5) * mouseSpeed * 60;

        // Use a single transform setting to avoid infinite string appending
        element.style.transform = `translate(${xMove}px, calc(${scrollMove}px + ${yMove}px))`;
      });

      rippleElements.forEach((element, index) => {
        const speed = (index + 1) * 0.05;
        const scrollMove = rate * speed;
        const scale = 1 + Math.sin(latestScrollY * 0.001) * 0.1;

        const mouseSpeed = (index + 1) * 0.01;
        const xMove = (latestMouseX - 0.5) * mouseSpeed * 40;
        const yMove = (latestMouseY - 0.5) * mouseSpeed * 40;

        // Single transform assignment
        element.style.transform = `translate(${xMove}px, calc(${scrollMove}px + ${yMove}px)) scale(${scale})`;
      });

      sections.forEach((section, index) => {
        const speed = (index + 1) * 0.03;
        const yPos = -(latestScrollY * speed);
        const scale = 1 + Math.sin(latestScrollY * 0.0005) * 0.02;
        section.style.transform = `translate3d(0, ${yPos}px, 0) scale(${scale})`;
      });

      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateTransforms);
        ticking = true;
      }
    };

    const handleScroll = () => {
      latestScrollY = window.pageYOffset;
      requestTick();
    };

    const handleMouseMove = (e) => {
      latestMouseX = e.clientX / window.innerWidth;
      latestMouseY = e.clientY / window.innerHeight;
      requestTick();
    };

    const handleResize = () => {
      // Reposition elements on window resize
      floatingElements.forEach(element => {
        const x = Math.random() * window.innerWidth;
        const y = Math.random() * window.innerHeight;
        element.style.left = `${x}px`;
        element.style.top = `${y}px`;
      });
      requestTick();
    };

    // Use passive event listeners for better scrolling performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    // Initial position trigger
    requestTick();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);

      [...floatingElements, ...rippleElements].forEach(element => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
    };
  }, [isDarkMode]);

  return (
    <div
      ref={containerRef}
      className="window-animation-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};

export default WindowAnimation;
